import React from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";

const SummaryTab = ({
  summaryText,
  setSummaryText,
  promptText,
  setPromptText,
  isEditingSummary,
  setIsEditingSummary,
  isFullScreenPrompt,
  setIsFullScreenPrompt,
  isGeneratingSummary,
  onSaveSummary,
  file,
}) => {
  const lilacBg = "#faf5ff";

  // Full screen prompt editor
  if (isFullScreenPrompt) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Full Screen Prompt Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            Custom Prompt
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPromptText("")}
              sx={{
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#64748b",
                fontSize: "12px",
                minWidth: "auto",
                px: 1.5,
                "&:hover": {
                  borderColor: "#cbd5e0",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                // TODO: Implement reset to aiAgent.defaultPrompt
              }}
              sx={{
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#64748b",
                fontSize: "12px",
                minWidth: "auto",
                px: 1.5,
                "&:hover": {
                  borderColor: "#cbd5e0",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPromptText(file?.prompt || file?.customPrompt || "");
                setIsFullScreenPrompt(false);
              }}
              sx={{
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#64748b",
                fontSize: "12px",
                minWidth: "auto",
                px: 1.5,
                "&:hover": {
                  borderColor: "#cbd5e0",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (onSaveSummary) {
                  onSaveSummary({
                    summary: summaryText,
                    prompt: promptText,
                  });
                }
                setIsFullScreenPrompt(false);
              }}
              sx={{
                textTransform: "none",
                backgroundColor: "#8b5cf6",
                fontSize: "12px",
                minWidth: "auto",
                px: 1.5,
                "&:hover": {
                  backgroundColor: "#7c3aed",
                },
              }}
            >
              Done
            </Button>
          </Box>
        </Box>

        {/* Full Screen Prompt Editor */}
        <TextField
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          placeholder="Enter custom instructions for the AI analysis..."
          multiline
          fullWidth
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              height: "100%",
              alignItems: "flex-start",
              backgroundColor: "#fffbeb",
              borderRadius: "8px",
              fontSize: "14px",
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": {
                borderColor: "#cbd5e0",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#8b5cf6",
                borderWidth: "2px",
              },
            },
            "& .MuiInputBase-input": {
              height: "100% !important",
              overflow: "auto !important",
              lineHeight: 1.6,
            },
          }}
        />
      </Box>
    );
  }

  // Normal summary view
  return (
    <Box
      sx={{
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: "100%",
        overflow: "auto",
        backgroundColor: lilacBg,
      }}
    >
      {/* Summary Section */}
      <Box
        sx={{
          backgroundColor: summaryText ? "#f5f3ff" : "#ffffff",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 0.75,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: summaryText ? "#f5f3ff" : "#f8fafc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            {summaryText && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#8b5cf6",
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: "11px", ml: 1 }}
            >
              ({summaryText ? "AI summary complete" : "Generate AI summary"})
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsEditingSummary((v) => !v)}
            sx={{
              width: 22,
              height: 22,
              backgroundColor: isEditingSummary ? "#8b5cf6" : "transparent",
              color: isEditingSummary ? "#ffffff" : "#94a3b8",
              "&:hover": {
                backgroundColor: isEditingSummary ? "#7c3aed" : "#e2e8f0",
              },
            }}
          >
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
        <TextField
          value={summaryText}
          onChange={(e) => setSummaryText(e.target.value)}
          placeholder={
            isGeneratingSummary
              ? "Generating..."
              : "Click AI button to generate"
          }
          multiline
          fullWidth
          disabled={!isEditingSummary}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              height: "100%",
              borderRadius: 0,
              backgroundColor: summaryText
                ? "#ffffffff"
                : isEditingSummary
                ? "#ffffffff"
                : "transparent",
              "& fieldset": { border: "none" },
              overflow: "auto",
              alignItems: "flex-start",
            },
            "& .MuiInputBase-input": {
              py: 1,
              px: 1.5,
              fontSize: "15.5px",
              lineHeight: 1.4,
              color: "#000000ff",
              overflow: "auto !important",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SummaryTab;
